import { useEffect, useState } from "react";
import { Bot, CalendarCheck, X, Clock, Video, MapPin } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { leadsApi } from "@/lib/crmApi";

/**
 * AI RDV proposal banner.
 *
 * Shown on a lead when the conversational agent has proposed a meeting
 * (lead.rdv_propose set, not yet confirmed). The admin reviews the
 * AI-suggested slot (free text, e.g. "samedi 7 juin 10h-11h") and either:
 *   - Confirme : pick the exact date/time → creates the real RDV (rdv_at)
 *     and clears the proposal.
 *   - Refuse   : drops the proposal, no RDV created.
 *
 * Africa/Dakar is UTC+0 year-round, so local ↔ ISO is effectively a no-op,
 * but we still go through a proper conversion to be safe.
 */
function toIsoUtc(localValue) {
  if (!localValue) return null;
  const d = new Date(localValue);
  if (isNaN(d.getTime())) return null;
  return d.toISOString();
}

export default function AiRdvProposalBanner({ lead, onUpdated }) {
  const [open, setOpen] = useState(false);
  const [datetime, setDatetime] = useState("");
  const [kind, setKind] = useState("physique");
  const [saving, setSaving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);

  // Map the agent's mode (physique | en_ligne) to the CRM rdv_kind
  // (physique | visio) used everywhere else.
  const proposedKind = lead?.rdv_propose_mode === "en_ligne" ? "visio" : "physique";

  useEffect(() => {
    if (open) {
      setDatetime("");
      setKind(proposedKind);
      setSubmitAttempted(false);
    }
  }, [open, proposedKind]);

  // Only render when there's an unconfirmed AI proposal.
  if (!lead?.rdv_propose || lead?.rdv_confirme) return null;

  const datetimeMissing = !datetime;

  const confirm = async () => {
    setSubmitAttempted(true);
    if (datetimeMissing) return;
    const iso = toIsoUtc(datetime);
    if (!iso) {
      toast.error("Date/heure invalide");
      return;
    }
    setSaving(true);
    try {
      const res = await leadsApi.confirmAiRdv(lead.id, iso, kind);
      toast.success("RDV confirmé · ajouté au calendrier");
      setOpen(false);
      onUpdated?.(res?.lead);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la confirmation");
    } finally {
      setSaving(false);
    }
  };

  const reject = async () => {
    setRejecting(true);
    try {
      const res = await leadsApi.rejectAiRdv(lead.id);
      toast.success("Proposition de RDV écartée");
      onUpdated?.(res?.lead);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setRejecting(false);
    }
  };

  const modeLabel = lead?.rdv_propose_mode === "en_ligne" ? "en ligne" : "physique";

  return (
    <>
      <div
        data-testid="ai-rdv-banner"
        className="mt-4 flex flex-col gap-3 rounded-xl border border-violet-300/70 bg-violet-50 p-4 shadow-soft sm:flex-row sm:items-center sm:justify-between dark:border-violet-500/30 dark:bg-violet-950/30"
      >
        <div className="flex items-start gap-3">
          <span className="flex h-9 w-9 flex-none items-center justify-center rounded-full bg-violet-600 text-white">
            <Bot className="h-4 w-4" />
          </span>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-violet-900 dark:text-violet-100">
              L'IA a proposé un rendez-vous — à valider
            </p>
            <p className="mt-0.5 text-sm text-violet-800/90 dark:text-violet-200/90">
              «&nbsp;{lead.rdv_propose}&nbsp;» · {modeLabel}
            </p>
          </div>
        </div>
        <div className="flex flex-none items-center gap-2 self-end sm:self-auto">
          <Button
            variant="outline"
            size="sm"
            onClick={reject}
            disabled={rejecting || saving}
            data-testid="ai-rdv-reject"
            className="gap-1.5"
          >
            <X className="h-3.5 w-3.5" />
            Refuser
          </Button>
          <Button
            size="sm"
            onClick={() => setOpen(true)}
            disabled={rejecting || saving}
            data-testid="ai-rdv-confirm-open"
            className="gap-1.5 bg-violet-600 hover:bg-violet-700"
          >
            <CalendarCheck className="h-3.5 w-3.5" />
            Confirmer le RDV
          </Button>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[440px]">
          <DialogHeader>
            <DialogTitle>Confirmer le rendez-vous</DialogTitle>
            <DialogDescription>
              L'IA a proposé «&nbsp;{lead.rdv_propose}&nbsp;». Fixez la date et
              l'heure exactes : le RDV apparaîtra dans le calendrier et les
              rappels s'activeront automatiquement.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div>
              <Label
                htmlFor="ai-rdv-datetime"
                className="flex items-center gap-1.5 text-xs"
              >
                <Clock className="h-3.5 w-3.5" />
                Date et heure
              </Label>
              <Input
                id="ai-rdv-datetime"
                type="datetime-local"
                value={datetime}
                onChange={(e) => setDatetime(e.target.value)}
                data-testid="ai-rdv-datetime-input"
                className={`mt-1.5 ${
                  submitAttempted && datetimeMissing ? "border-red-500" : ""
                }`}
              />
              {submitAttempted && datetimeMissing && (
                <p className="mt-1 text-xs text-red-500" data-testid="ai-rdv-datetime-error">
                  Choisissez une date et une heure.
                </p>
              )}
            </div>

            <div>
              <Label className="text-xs">Type de rendez-vous</Label>
              <Select value={kind} onValueChange={setKind}>
                <SelectTrigger className="mt-1.5" data-testid="ai-rdv-kind-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="physique">
                    <span className="flex items-center gap-2">
                      <MapPin className="h-3.5 w-3.5" /> Physique (showroom / site)
                    </span>
                  </SelectItem>
                  <SelectItem value="visio">
                    <span className="flex items-center gap-2">
                      <Video className="h-3.5 w-3.5" /> En ligne (visio)
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={saving}>
              Annuler
            </Button>
            <Button
              onClick={confirm}
              disabled={saving}
              data-testid="ai-rdv-confirm-save"
              className="bg-violet-600 hover:bg-violet-700"
            >
              {saving ? "Confirmation…" : "Confirmer le RDV"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
